import type { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import {
  ArticleJsonLd,
  BreadcrumbJsonLd,
  FAQPageJsonLd,
  HowToJsonLd,
  NextSeo
} from 'next-seo'
import dynamic from 'next/dynamic'
import Footer from 'src/components/Footer'
import Article from 'src/components/Help/Article'
import NavBar from 'src/components/LandingNav'
import { DEFAULT_STRUCTURED_DATA } from 'src/constants/structuredData'
import { IPost } from 'src/types/hygraph'
import {
  HygraphTopics,
  TOPIC_MAP,
  getPost,
  getPostTopic
} from 'src/utils/hygraph'
import loggerServer from 'src/utils/loggerServer'
import {
  HYGRAPH_PAGE_REVALIDATE_SECONDS,
  getLocalePrefix,
  getPostAuthor,
  getPostDates,
  withCleanTitle
} from 'src/utils/postSeo'
import { createSEODescriptions, toOgLocale } from 'src/utils/seo'

const WhatsAppButton = dynamic(() => import('src/components/WhatsappButton'))

const image = 'https://www.spotme.mx/images/cover.png'

type IProps = {
  topic: HygraphTopics
  post: IPost
  locale: string
  optimizedDescription: string
  optimizedDescriptionOg: string
  /** Raw CMS title for <title>/og:title — kept unstripped (no-title-churn rule). */
  metaTitle: string
}

const Page: NextPage<IProps> = ({
  post,
  topic,
  locale,
  optimizedDescription,
  optimizedDescriptionOg,
  metaTitle
}) => {
  const { t } = useTranslation('common')
  if (!post || !topic) return null
  const { title, slug, titleOg, structuredData } = post
  const { path, label } = TOPIC_MAP[topic]
  const url = `https://www.spotme.mx${path}/${slug}`
  const fullTitle = 'SpotMe | ' + metaTitle
  const titleString =
    fullTitle.length > 60 ? (metaTitle ?? '').slice(0, 60) : fullTitle
  const sd = Array.isArray(structuredData) ? structuredData : []
  const FAQs = sd.find(({ type }: { type: string }) => type === 'FAQ') as
    | { type: 'FAQ'; items: { title: string; description: string }[] }
    | undefined
  const HowTo = sd.find(({ type }: { type: string }) => type === 'HowTo') as
    | {
        type: 'HowTo'
        name?: string
        totalTime?: string
        steps: { name: string; text: string; url?: string }[]
      }
    | undefined
  const ogImage = post.coverImage?.url ?? image
  const { datePublished, dateModified } = getPostDates(post)
  const author = getPostAuthor(post)

  return (
    <div>
      <NextSeo
        title={titleString}
        description={optimizedDescription}
        canonical={url}
        openGraph={{
          type: 'article',
          url,
          title: titleOg ?? metaTitle,
          description: optimizedDescriptionOg,
          locale: toOgLocale(locale),
          images: [
            {
              url: post.coverImage?.url ?? image,
              width: 1200,
              height: 630,
              alt: title
            }
          ],
          article: {
            publishedTime: datePublished,
            modifiedTime: dateModified,
            authors: [author.name],
            section: label
          }
        }}
      />
      <ArticleJsonLd
        type="Article"
        url={url}
        title={title}
        images={[post.coverImage?.url ?? image]}
        datePublished={datePublished}
        dateModified={dateModified}
        authorName={author}
        publisherLogo={DEFAULT_STRUCTURED_DATA.logo}
        publisherName={DEFAULT_STRUCTURED_DATA.name}
        description={optimizedDescription}
        isAccessibleForFree
      />
      <BreadcrumbJsonLd
        itemListElements={[
          {
            position: 1,
            name: 'Ayuda',
            item: 'https://www.spotme.mx/acerca-de-spotme'
          },
          {
            position: 2,
            name: label,
            item: `https://www.spotme.mx${path}`
          },
          {
            position: 3,
            name: title,
            item: url
          }
        ]}
      />
      {FAQs && (
        <FAQPageJsonLd
          mainEntity={FAQs.items.map(({ title, description }) => ({
            questionName: title,
            acceptedAnswerText: description
          }))}
        />
      )}
      {HowTo && HowTo.steps?.length > 0 && (
        <HowToJsonLd
          name={HowTo.name ?? title}
          image={ogImage}
          totalTime={HowTo.totalTime}
          step={HowTo.steps.map((s) => ({
            name: s.name,
            text: s.text,
            url: s.url
          }))}
        />
      )}

      <main>
        <NavBar />
        <Article post={post} topic={topic} />
        <Footer />
        <WhatsAppButton msg={t('HELP.ARTICLE_WHATSAPP_MSG', { title })} />
      </main>
    </div>
  )
}

export default Page

type IParams = { slug: string; topic: HygraphTopics }

export const getStaticPaths = (async () => {
  // Help center traffic is low — skip build-time prerender and let pages
  // render on first request (cached via ISR after). Invalid slugs fall
  // through to the `notFound: true` branch in getStaticProps.
  return { paths: [], fallback: 'blocking' }
}) satisfies GetStaticPaths

const NOT_FOUND = {
  notFound: true,
  revalidate: HYGRAPH_PAGE_REVALIDATE_SECONDS
} as const

export const getStaticProps: GetStaticProps<IProps> = async (context) => {
  const { locale = 'es', params } = context
  const { slug, topic } = params as IParams

  // No module-level slug allow-list: it was cached for the life of the server
  // instance, so articles published after warm-up 404'd.
  const response = await getPost(slug, locale)
  const rawPost: IPost | null = response?.data?.post ?? null
  if (!rawPost) {
    loggerServer.warn('Error retrieving post from hygraph.', {
      slug,
      topic,
      response
    })
    return NOT_FOUND
  }
  const post = withCleanTitle(rawPost)

  // 301-redirect to the article's real canonical URL when the URL-passed
  // topic doesn't match the post's actual topic. The `[topic]/[slug]` route
  // resolves any slug regardless of topic, which silently created duplicate
  // indexable URLs for blog articles (e.g. /acerca-de-spotme/faqs/{slug} for
  // articles whose real topic is blog-mini-bodega). Ahrefs flagged 29 such
  // pages as "Indexable not in sitemap" in June 2026.
  const actualTopic = getPostTopic(post).topic
  if (actualTopic && actualTopic.topic !== topic) {
    const canonical =
      actualTopic.path === '/blog' && actualTopic.pillar
        ? actualTopic.pillar === 'anfitrion'
          ? `/anfitrion/blog/${slug}`
          : `/${actualTopic.pillar}/blog/${slug}`
        : `${actualTopic.path}/${slug}`
    return {
      redirect: {
        destination: `${getLocalePrefix(locale)}${canonical}`,
        permanent: true
      },
      revalidate: HYGRAPH_PAGE_REVALIDATE_SECONDS
    }
  }

  const { description } = post
  const title = rawPost.title
  // Optimize meta descriptions for SEO
  const {
    description: optimizedDescription,
    descriptionOg: optimizedDescriptionOg
  } = createSEODescriptions(
    description,
    `Aprende sobre ${title} con SpotMe. Encuentra información útil y consejos prácticos.`
  )

  const translations = await serverSideTranslations(locale ?? 'es', [
    'common',
    'footer',
    'nav',
    'platform_stats',
    'auth'
  ])
  return {
    props: {
      post,
      topic,
      locale,
      ...translations,
      optimizedDescription,
      optimizedDescriptionOg,
      metaTitle: rawPost.title ?? ''
    },
    revalidate: HYGRAPH_PAGE_REVALIDATE_SECONDS
  }
}
